import { useEffect, useRef } from 'react'

class DangerousDependencyError extends Error {
  constructor() {
    super(
      `One of the depencencies is an object or array, which might cause rendering issues. 
       Suggestions : pass only primitives as dependencies of useEffect, or use the custom hook useDeepCompareEffect.`
    )
    this.name = 'DangerousDependencyError'
  }
}

type Primitive = number | boolean | string | null | undefined

/* Differences with respect to actual useEffect :
   1. If dependencies is undefined, the default value of [] is used. In this
      case, the useEffect hook will run only on first render.
   2. We throw an error if an object or an array is passed as a dependency.
      Why ? To avoid cases where infinite renders are likely.
   3. We pass the current mount status to the callback
*/
export default function (
  callback: (isMounted: () => boolean) => any | void,
  dependencies: Primitive[] | undefined = []
): void {
  for (const dependency of dependencies) {
    if (typeof dependency === 'object' && dependency !== null) {
      throw new DangerousDependencyError()
    }
  }

  // FIXME (tlaurent 2022-11-14): This useRef will create a race condition
  // if multiple useSafeEffect are used in 1 component
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true

    callback(() => mountedRef.current)

    return () => {
      mountedRef.current = false
    }
  }, dependencies)
}
